// Decode / Research. New layout (user 7 Oct 2026): the sound types are on top (grouped Tonal / Pulsed / Other) and
// replace the mock's first steps (Data, Category); under them the part of the portal to work in — Review, Results,
// Export — and the data source (My data / Community / To approve).
// Tonal sections → CallReview, pulsed sections → PulsedReview. 7 Oct 2026.
// Final results (user 7 Oct): a researcher's or admin's Confirm is final. A decoder's Confirm is "sent for approval":
// it waits in "To approve" until a researcher or admin checks it (seeing the decoder's version), changes what is
// needed and approves it. Only final reviews go to results and the training set.
import React, { useEffect, useState } from 'react';
import CallReview from './CallReview';
import PulsedReview from './PulsedReview';
import ResultsView from './ResultsView';
import { LibraryView } from './Library';
import NoiseView from './NoiseView';
import AboutPage from '../../common/AboutPage';
import { SECTIONS } from './sections';
import { SCOPES, SCOPE_NOTE } from './reviewData';
import './CallReview.css';

const NONE = [];

// a fault in one view shows here (with the message) instead of a blank page
class Guard extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('Analysis view failed', err, info?.componentStack); } // eslint-disable-line no-console
  render() {
    if (!this.state.err) return this.props.children;
    return (<div className="ct-card cr-error" role="alert"><strong>This view stopped with an error.</strong>
      <p className="hcr-mono hcr-small">{String(this.state.err?.message || this.state.err)}</p>
      <button type="button" className="ct-btn" onClick={() => this.setState({ err: null })}>Try again</button></div>);
  }
}
export const isFinalRole = (role) => role === 'researcher' || role === 'admin';


// recordings grouped by recording day (newest first) for the Recording picker (10 Oct 2026)
const dayOf = (r) => { const ms = r.recordedMs || r.addedMs; return ms ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'date unknown'; };
function groupByDay(recs) { const m = new Map(); recs.forEach((r) => { const d = dayOf(r); if (!m.has(d)) m.set(d, []); m.get(d).push(r); }); return [...m.entries()]; }
function recLabel(r) {
  const ms = r.recordedMs || r.addedMs; const tm = ms ? new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
  return `Station ${r.stationId}${tm ? ` · ${tm}` : ''} · ${r.file || r.fileId} · ${r.n} to review`;
}

export default function AnalysisWorkspace({ source, role: role0 = 'decoder', initialSection = 'humpback' }) {
  const [role, setRole] = useState(role0);
  const [section, setSection] = useState(initialSection);
  const [scope, setScope] = useState(source.canMine ? 'mine' : 'community');
  const [mode, setMode] = useState('review');                 // review | results | export
  const [calls, setCalls] = useState([]);
  const [anyKind, setAnyKind] = useState(null);           // Unsorted: 'pieces' (clicks) or 'calls' (tonal)
  const [state, setState] = useState('loading');
  const [loadedFor, setLoadedFor] = useState('');             // the list in `calls` belongs to this section / scope
  const [savedN, setSavedN] = useState(0);
  const [savedRec, setSavedRec] = useState({});          // saved in this visit, per recording (progress)
  const savedHere = (r) => savedRec[r.key] || 0;
  const [recording, setRecording] = useState(null);       // station|file of the recording shown (null = newest)
  const [recs, setRecs] = useState(null);                 // recordings with open items, newest first
  const finalRole = isFinalRole(role);
  if (source.library && source.library.setViewer) source.library.setViewer(role);   // preview only (live: the rules decide)
  // admin also sees every station's data (private ones too); researchers / admins approve decoders' reviews
  const base = role === 'admin' ? [...SCOPES, ['all', 'All stations']] : SCOPES;
  const scopes = finalRole && mode === 'review' ? [...base, ['approve', 'To approve']] : base;

  useEffect(() => { if (((!finalRole || mode !== 'review') && scope === 'approve') || (role !== 'admin' && scope === 'all')) setScope('community'); }, [finalRole, scope, mode, role]);
  useEffect(() => {
    if (mode !== 'review') return undefined;
    let live = true;
    setState('loading');
    source.list(section, scope, recording)
      .then((c) => { if (live) { setCalls(c); setRecs(c.recordings || null); setLoadedFor(`${section}|${scope}`); setState('ready'); } })
      .catch((e) => { if (live) { setCalls([]); setState(`error: ${e.message || e}`); } });
    return () => { live = false; };
  }, [source, section, scope, mode, recording]);
  useEffect(() => { setRecording(null); setRecs(null); }, [section, scope]);

  const download = () => {
    const blob = new Blob([JSON.stringify(source.saved || [], null, 1)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'call_reviews.json'; a.click();
  };
  const cur = SECTIONS.find((s) => s.key === section);
  const approving = scope === 'approve';
  const confirmText = approving ? 'Approve (final)' : (finalRole ? 'Confirm (final)' : 'Send for approval');
  const note = approving
    ? 'You see the reviewer’s version. Change what is needed; approving makes it final (results + training set).'
    : (finalRole ? 'Your confirmation is final: it goes to the results and the training set.'
      : 'A researcher or admin checks your review and makes it final.');
  const scopeNote = approving ? 'Reviews sent by decoders, waiting for a researcher or admin.'
    : scope === 'all' ? SCOPE_NOTE.all : (source.canMine ? SCOPE_NOTE[scope] : `${SCOPE_NOTE.community} My data needs a station plan.`);

  const groups = [...new Set(SECTIONS.map((x) => x.group))];
  const header = (
    <>
      {mode !== 'noise' && <div className="aw-types" role="tablist" aria-label="Sound types">
        {groups.map((g) => (
          <div key={g} className="aw-group">
            <div className="aw-group-name">{g}</div>
            <div className="aw-group-tabs">
              {SECTIONS.filter((x) => x.group === g).map((x) => (
                <button key={x.key} type="button" role="tab" aria-selected={section === x.key} className={`aw-type ${section === x.key ? 'on' : ''}`}
                        onClick={() => setSection(x.key)}>{x.label}</button>))}
            </div>
          </div>))}
      </div>}
      <div className="aw-bar">
        <div className="aw-modes" role="tablist" aria-label="Part of the portal">
          {[['review', approving ? 'Approve' : 'Review'], ['results', 'Results'], ['export', 'Export'], ...(source.library ? [['library', 'Library']] : []), ...(source.noise ? [['noise', 'Noise']] : []), ['about', 'ℹ️ About']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={mode === k} className={`aw-mode ${mode === k ? 'on' : ''}`} onClick={() => setMode(k)}>{l}</button>))}
        </div>
        {mode !== 'noise' && mode !== 'about' && <div className="aw-scope" role="group" aria-label="Data source">
          {scopes.map(([k, label]) => (
            <button key={k} type="button" className={scope === k ? 'on' : ''} aria-pressed={scope === k}
                    disabled={k === 'mine' && !source.canMine} onClick={() => setScope(k)}>{label}</button>))}
        </div>}
        {/* 14 Oct 2026: community Discord — decoders and researchers have their own servers */}
        <a className="ct-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer"
           href={role === 'decoder' ? 'https://discord.gg/SRaTVJdQQf' : 'https://discord.gg/MUaJ6gEeZU'}
           title={role === 'decoder' ? 'Decoder community on Discord' : 'Researcher community on Discord'}>
          💬 {role === 'decoder' ? 'Decoder' : 'Researcher'} Discord
        </a>
      </div>
      {mode === 'review' && recs && recs.length > 0 && (
        <div className="aw-recs">
          <label className="hcr-small">Recording
            <select className="hcr-input" value={calls.recording || ''} onChange={(e) => setRecording(e.target.value)}>
              {groupByDay(recs).map(([day, rs]) => (
                <optgroup key={day} label={day}>
                  {rs.map((r) => <option key={r.key} value={r.key}>{recLabel(r)}</option>)}
                </optgroup>))}
            </select></label>
          {(() => { const cur = recs.find((r) => r.key === calls.recording) || recs[0]; const tot = cur.n + (cur.done || 0) + savedHere(cur);
            const done = (cur.done || 0) + savedHere(cur);
            return <span className="aw-prog hcr-small"><progress max={tot} value={done} /> {done} of {tot} done in this recording</span>; })()}
          {(() => { const i = recs.findIndex((r) => r.key === calls.recording); return i >= 0 && i < recs.length - 1
            ? <button type="button" className="ct-btn" onClick={() => setRecording(recs[i + 1].key)}>Next recording ▸</button> : null; })()}
          <span className="ct-muted hcr-small">{recs.length} recording{recs.length === 1 ? '' : 's'} with items to review · {recs.reduce((n, r) => n + r.n, 0)} items</span>
        </div>)}
      <div className="hcr-head">
        <div>
          <div className="ct-muted hcr-kicker">{role === 'admin' ? 'Research (admin)' : finalRole ? 'Research' : 'Decode'} · {mode === 'noise' ? 'Noise levels of the stations' : `${cur.group} · ${cur.label}`}</div>
          <h2 className="ct-title">{mode === 'about' ? 'About the analysis' : mode === 'noise' ? 'Noise levels' : mode === 'library' ? `Call library — ${cur.label}` : mode === 'results' ? `Results — ${cur.long || cur.label}` : mode === 'export' ? `Export — ${cur.label}`
            : (approving ? 'Reviews waiting for approval' : 'Found by the engine — check, draw and adjust')}</h2>
        </div>
        {mode !== 'noise' && mode !== 'about' && <span className="ct-muted aw-note">{scopeNote}</span>}
      </div>
      {source.demo && (
        <div className="aw-demo">PREVIEW with example data from the engine — reviews stay in this page. {savedN} saved.
          {' '}Preview as:{' '}
          {['decoder', 'researcher', 'admin'].map((r) => (
            <button key={r} type="button" className={`cr-mini ${role === r ? 'aw-on' : ''}`} aria-pressed={role === r} onClick={() => setRole(r)}>{r}</button>))}
          {savedN > 0 && <> <button type="button" className="cr-mini" onClick={download}>Download saved reviews (JSON)</button></>}</div>)}
      {mode === 'review' && state === 'loading' && <p className="ct-muted">Loading…</p>}
      {mode === 'review' && state.startsWith('error') && <p className="cr-error">Could not load ({state.slice(7)}).</p>}
    </>
  );
  if (mode === 'noise' && source.noise) {
    const nscopes = [...(source.canMine ? [['mine', 'My stations']] : []), ['community', 'Community'], ...(role === 'admin' ? [['all', 'All stations']] : [])];
    return <div className="hcr">{header}<Guard key="g-noise"><NoiseView key={`noise-${role}`} source={source.noise} scopes={nscopes} initialScope={source.canMine ? 'mine' : 'community'} /></Guard></div>;
  }
  if (mode === 'about') {
    return <div className="hcr">{header}<AboutPage /></div>;
  }
  if (mode === 'library' && source.library) {   // decoders too: their own library + the community library (13 Oct 2026)
    return <div className="hcr">{header}<Guard key={`g-lib-${section}`}><LibraryView library={source.library} sectionKey={section} role={role} /></Guard></div>;
  }
  if (mode !== 'review') {
    return <div className="hcr">{header}<Guard key={`g-${section}-${mode}`}><ResultsView key={`${section}-${scope}-${mode}-${role}`} source={source} sectionKey={section} scope={scope} mode={mode} level={role} /></Guard></div>;
  }
  // Unsorted holds tonal calls AND clicks pieces (12 Oct 2026: a clicks piece opened in the call screen crashed —
  // "Cannot read properties of undefined (reading 'toFixed')"): each kind opens in its own screen
  const loaded = state === 'ready' && loadedFor === `${section}|${scope}` ? calls : NONE;
  const isPiece = (x) => x._item?.kind === 'piece' || x.family === 'pulsed' || (!x.call && !!x.clicks);
  let shown = loaded, pulsed = cur.signal === 'pulsed', kindBar = null;
  if (cur.signal === 'any') {
    const pieces = loaded.filter(isPiece), tonal = loaded.filter((x) => !isPiece(x));
    const fits = (anyKind === 'pieces' && pieces.length) || (anyKind === 'calls' && tonal.length);
    const kind = fits ? anyKind : (pieces.length && !tonal.length ? 'pieces' : 'calls');
    pulsed = kind === 'pieces'; shown = Object.assign(pulsed ? pieces : tonal, { recording: loaded.recording, recordings: loaded.recordings });
    if (pieces.length && tonal.length) {
      kindBar = (
        <div className="aw-kinds" role="group" aria-label="Kind of items">
          <button type="button" className={`ct-btn ${!pulsed ? 'aw-on' : ''}`} aria-pressed={!pulsed} onClick={() => setAnyKind('calls')}>Calls ({tonal.length})</button>
          <button type="button" className={`ct-btn ${pulsed ? 'aw-on' : ''}`} aria-pressed={pulsed} onClick={() => setAnyKind('pieces')}>Clicks pieces ({pieces.length})</button>
        </div>);
    }
  }
  const Review = pulsed ? PulsedReview : CallReview;
  return (
    <Guard key={`g-${section}-${scope}-${role}-${pulsed ? 'p' : 'c'}`}><Review key={`${section}-${scope}-${role}-${pulsed ? 'p' : 'c'}`} sectionKey={section} calls={shown} header={<>{header}{kindBar}</>}
            empty={state === 'ready' ? (approving ? 'Nothing waiting for approval in this section.' : 'Nothing waiting for review in this section.') : null}
            sourceLabel={`${calls.length} item${calls.length === 1 ? '' : 's'} · ${scopes.find((x) => x[0] === scope)?.[1]}`}
            confirmText={confirmText} finalStatus={finalRole ? 'confirmed' : 'to_approve'} note={note} level={role}
            library={source.library}
            onSubmit={async (entry, review) => {
              const doc = { ...review, final: review.status === 'confirmed' || (review.status === 'dismissed' && finalRole), reviewerRole: role,
                approvesReview: approving ? (entry._prevId || null) : null };
              await source.submit(entry, doc, { approving, finalRole });
              setSavedN((n) => n + 1);
              if (calls.recording) setSavedRec((m) => ({ ...m, [calls.recording]: (m[calls.recording] || 0) + 1 }));
            }} /></Guard>
  );
}
